import React, { useState } from 'react';
import { Link } from './Link';
import { classNames } from '@fullstory/util/fmt';
import css from '../index.module.scss';
import { GraphLink } from '../types';
import { JourneyMapSettings } from '@fullstory/proto/fs/services/search-settings/visualizationsettings';
import { generateGetBoundingClientRect, getNodeHash, interactionDirectionOffset } from '../util';
import { VirtualElement } from '@popperjs/core';

interface LinksProps {
  links: GraphLink[];
  direction: JourneyMapSettings.Direction;
  canAppendInteraction: boolean;
  isInPath: (l: GraphLink) => boolean;
  isInIntelligentPath: (sourceNodeId: string, targetNodeId: string) => boolean;
  dropinOffsets: { [identAtDepth: string]: number };
  inSameSession: boolean;
  selectedLinks: { [key: string]: GraphLink };
  maxDepth: number;
}

const Links: React.FC<LinksProps> = ({
  links,
  direction,
  canAppendInteraction,
  isInPath,
  dropinOffsets,
  inSameSession,
  selectedLinks,
  isInIntelligentPath,
  maxDepth,
}) => {
  const [virtualElement, setVirtualElement] = useState<VirtualElement>({
    getBoundingClientRect: generateGetBoundingClientRect(),
  });

  const _setVirtualElement = (e: React.MouseEvent) => {
    setVirtualElement({
      getBoundingClientRect: generateGetBoundingClientRect(e.clientX, e.clientY),
    });
  };

  const allLinks: React.ReactElement[] = [];
  const offsetMap: { [key: string]: number } = {};
  for (let i = 0; i < links.length; i++) {
    const currLink = links[i];
    if (!currLink) continue;
    const link = {
      ...currLink,
      value: selectedLinks[currLink.getId()]?.value ?? currLink.value,
    };
    const isBackwards = direction === JourneyMapSettings.Direction.DIRECTION_BACKWARD;
    const isSelected = selectedLinks[link.getId()] !== undefined;
    const sourceOffsetKey = `source-${link.source.getId()}`;
    const targetOffsetKey = `target-${link.target.getId()}`;

    let sourceOffset = offsetMap[sourceOffsetKey];
    if (!sourceOffset || isNaN(sourceOffset)) {
      sourceOffset = 0;
      offsetMap[sourceOffsetKey] = sourceOffset;
    }

    let targetOffset = offsetMap[targetOffsetKey];
    if (!targetOffset || isNaN(targetOffset)) {
      targetOffset = 0;
      offsetMap[targetOffsetKey] = targetOffset;
    }

    allLinks.push(
      <g key={link.getId()} className={classNames({ [css.dimmed]: !isInPath(link) })}>
        <Link
          link={link}
          sourceOffset={sourceOffset}
          targetOffset={targetOffset + (dropinOffsets[`${link.target.getId()}`] ?? 0)}
          offset={interactionDirectionOffset(direction, canAppendInteraction)}
          virtualAnchor={virtualElement}
          onMouseMove={_setVirtualElement}
          inSameSession={inSameSession}
          direction={direction}
          isIntelligentPath={
            isSelected &&
            isInIntelligentPath(
              getNodeHash(
                link.source.identHash,
                isBackwards ? maxDepth - link.source.depth : link.source.depth,
              ),
              getNodeHash(
                link.target.identHash,
                isBackwards ? maxDepth - link.target.depth : link.target.depth,
              ),
            )
          }
        />
      </g>,
    );

    // defined checks above for both entries
    offsetMap[sourceOffsetKey]! += link.height;
    offsetMap[targetOffsetKey]! += link.height;
  }

  return <>{allLinks}</>;
};

export { Links };
